/**
 * Exact hindsight-optimal 10-team list (and optional swap) for a settled MatchDay.
 *
 * Maximises the real list score at `atMatchday` with mdReset=0 over every valid
 * WC-26 list (≤4 favourites, ≥2 outsiders) and every legal swap (none, or one
 * pair of ranks after SWAP_MATCHDAY).
 *
 * Step 1 (no-swap / K ≤ S): best category mix by P_K, sorted by P_K.
 * Step 2 (K > S): for each of 45 rank swaps and each ordered team pair on the
 * crossed ranks, fill the other eight optimally by P_K under global category caps.
 *
 * Used by Cloud Functions public stats and the Stats page.
 */
import { MATCHDAY_COUNT, SWAP_MATCHDAY } from '../../constants';
import {
  Country,
  countryCategory,
  tournamentCountriesInfo,
} from '../../models/country';
import type { gameResult } from '../../models/gameUpdate';
import type { ProgressionAward, TeamMatrix } from '../../models/scoring';
import { applySwapToList, buildCPM, buildMPM } from '../scoring/matchdayScoring';

export type IdealListRow = {
  country: Country;
  /** Cumulative team points through asOfMatchday (mdReset baseline). */
  rawPoints: number;
  /** Points earned through swapMatchday (pre-swap segment). */
  pointsBefore: number;
  /** Points earned after swapMatchday through asOfMatchday. */
  pointsAfter: number;
  /** Pre-swap list-rank multiplier (10 for rank 1 … 1 for rank 10). */
  multiplier: number;
  /** Post-swap list-rank multiplier (equals multiplier when no swap / not moved). */
  multiplierAfter: number;
  /**
   * Score contribution for this team:
   * pointsBefore × multiplier + pointsAfter × multiplierAfter.
   * Sum(contributions) === score.
   */
  contribution: number;
};

export type IdealListResult = {
  countryList: Country[];
  /** Null when no swap, or when atMatchday ≤ swapMatchday. */
  swapPair: [number, number] | null;
  score: number;
  asOfMatchday: number;
  rows: IdealListRow[];
};

/**
 * Per-rank arithmetic for the ideal list UI.
 * Faithful to computeScoreHistory: contribution =
 *   pointsBefore × multiplier + pointsAfter × multiplierAfter
 * (mdReset default 0).
 */
export function buildIdealListRows(params: {
  countryList: Country[];
  swapPair: [number, number] | null;
  asOfMatchday: number;
  cpm: TeamMatrix;
  swapMatchday: number;
  mdReset?: number;
}): IdealListRow[] {
  const { countryList, swapPair, asOfMatchday: K, cpm, swapMatchday: S } = params;
  const mdReset = params.mdReset ?? 0;
  if (countryList.length !== 10) return [];

  const afterList = applySwapToList(countryList, swapPair);
  const rows: IdealListRow[] = [];

  for (let i = 0; i < 10; i++) {
    const country = countryList[i];
    const row = cpm[country];
    const multiplier = 10 - i;
    const afterIdx = afterList.indexOf(country);
    const multiplierAfter = afterIdx >= 0 ? 10 - afterIdx : multiplier;

    let pointsBefore = 0;
    let pointsAfter = 0;
    let contribution = 0;
    if (row) {
      const beforeHi = Math.min(K, S);
      if (beforeHi > mdReset) {
        pointsBefore = row[beforeHi] - row[mdReset];
        contribution += multiplier * pointsBefore;
      }
      if (K > S) {
        pointsAfter = row[K] - row[Math.max(S, mdReset)];
        contribution += multiplierAfter * pointsAfter;
      }
    }

    const rawPoints = pointsBefore + pointsAfter;
    rows.push({
      country,
      rawPoints,
      pointsBefore,
      pointsAfter,
      multiplier,
      multiplierAfter,
      contribution,
    });
  }

  return rows;
}

export type ComputeIdealListOptions = {
  atMatchday: number;
  progressionAwards?: ProgressionAward[];
  matchdayCount?: number;
  swapMatchday?: number;
};

type CategoryPools = {
  fav: Country[];
  mid: Country[];
  out: Country[];
  all: Country[];
};

function tournamentPoolByCategory(): CategoryPools {
  const fav: Country[] = [];
  const mid: Country[] = [];
  const out: Country[] = [];
  for (const id of Object.keys(tournamentCountriesInfo) as Country[]) {
    if (id === Country.TBD) continue;
    const cat = tournamentCountriesInfo[id]?.category;
    if (cat === countryCategory.Favourite) fav.push(id);
    else if (cat === countryCategory.Intermediate) mid.push(id);
    else if (cat === countryCategory.Outsider) out.push(id);
  }
  return { fav, mid, out, all: [...fav, ...mid, ...out] };
}

function sortByPoints(teams: Country[], points: (t: Country) => number): Country[] {
  return [...teams].sort((a, b) => {
    const d = points(b) - points(a);
    if (d !== 0) return d;
    return a.localeCompare(b);
  });
}

function mult(rank: number): number {
  return 10 - rank;
}

function categoryOf(t: Country): countryCategory | undefined {
  return tournamentCountriesInfo[t]?.category;
}

function countUsed(a: Country, b: Country): { f: number; o: number; i: number } {
  let f = 0;
  let o = 0;
  let i = 0;
  for (const t of [a, b]) {
    const cat = categoryOf(t);
    if (cat === countryCategory.Favourite) f += 1;
    else if (cat === countryCategory.Outsider) o += 1;
    else if (cat === countryCategory.Intermediate) i += 1;
  }
  return { f, o, i };
}

/** Take the first `n` teams from a P_K-sorted list, skipping excludes. */
function takeTopExcluding(
  sorted: Country[],
  n: number,
  exclude: Set<Country>,
): Country[] | null {
  if (n === 0) return [];
  const out: Country[] = [];
  for (const t of sorted) {
    if (exclude.has(t)) continue;
    out.push(t);
    if (out.length === n) return out;
  }
  return null;
}

function scoreNoSwap(list: Country[], pK: (t: Country) => number): number {
  let s = 0;
  for (let r = 0; r < 10; r++) {
    s += mult(r) * pK(list[r]);
  }
  return s;
}

function scoreWithSwap(
  list: Country[],
  i: number,
  j: number,
  pPre: (t: Country) => number,
  pPost: (t: Country) => number,
  pK: (t: Country) => number,
): number {
  const A = list[i];
  const B = list[j];
  let s =
    mult(i) * pPre(A) +
    mult(j) * pPost(A) +
    mult(j) * pPre(B) +
    mult(i) * pPost(B);
  for (let r = 0; r < 10; r++) {
    if (r === i || r === j) continue;
    s += mult(r) * pK(list[r]);
  }
  return s;
}

type BestState = {
  countryList: Country[];
  swapPair: [number, number] | null;
  score: number;
};

/**
 * Step 1: exact no-swap optimum — best legal category mix by P_K, sorted by P_K.
 */
function bestNoSwap(
  favSorted: Country[],
  midSorted: Country[],
  outSorted: Country[],
  pK: (t: Country) => number,
): BestState | null {
  let best: BestState | null = null;

  for (let o = 2; o <= Math.min(8, outSorted.length); o++) {
    for (let f = 0; f <= Math.min(4, 10 - o); f++) {
      const midCount = 10 - o - f;
      if (midCount < 0 || midCount > midSorted.length) continue;
      if (f > favSorted.length) continue;

      const pick = [
        ...outSorted.slice(0, o),
        ...favSorted.slice(0, f),
        ...midSorted.slice(0, midCount),
      ];
      if (pick.length !== 10) continue;

      const list = sortByPoints(pick, pK);
      const score = scoreNoSwap(list, pK);
      if (!best || score > best.score) {
        best = { countryList: list, swapPair: null, score };
      }
    }
  }

  return best;
}

/**
 * Step 2: exact one-swap optimum over all rank pairs and crossed team pairs.
 */
function bestWithOneSwap(
  pool: CategoryPools,
  favSorted: Country[],
  midSorted: Country[],
  outSorted: Country[],
  pPre: (t: Country) => number,
  pPost: (t: Country) => number,
  pK: (t: Country) => number,
): BestState | null {
  let best: BestState | null = null;
  const { all } = pool;

  for (let i = 0; i < 10; i++) {
    for (let j = i + 1; j < 10; j++) {
      // Ordinary ranks in descending-multiplier order (ascending index).
      const ordinaryRanks: number[] = [];
      for (let r = 0; r < 10; r++) {
        if (r !== i && r !== j) ordinaryRanks.push(r);
      }
      ordinaryRanks.sort((a, b) => a - b);

      for (const A of all) {
        for (const B of all) {
          if (A === B) continue;

          const used = countUsed(A, B);
          const exclude = new Set<Country>([A, B]);
          const nF = favSorted.length - used.f;
          const nO = outSorted.length - used.o;
          const nI = midSorted.length - used.i;
          const fMax = 4 - used.f;
          const oMin = Math.max(0, 2 - used.o);

          for (let fPrime = 0; fPrime <= Math.min(fMax, nF, 8); fPrime++) {
            for (
              let oPrime = oMin;
              oPrime <= Math.min(8 - fPrime, nO);
              oPrime++
            ) {
              const iPrime = 8 - fPrime - oPrime;
              if (iPrime < 0 || iPrime > nI) continue;

              const outs = takeTopExcluding(outSorted, oPrime, exclude);
              const favs = takeTopExcluding(favSorted, fPrime, exclude);
              const mids = takeTopExcluding(midSorted, iPrime, exclude);
              if (!outs || !favs || !mids) continue;

              const eight = sortByPoints([...outs, ...favs, ...mids], pK);
              const list = new Array<Country>(10);
              list[i] = A;
              list[j] = B;
              for (let k = 0; k < 8; k++) {
                list[ordinaryRanks[k]] = eight[k];
              }

              const score = scoreWithSwap(list, i, j, pPre, pPost, pK);
              if (!best || score > best.score) {
                best = { countryList: list, swapPair: [i, j], score };
              }
            }
          }
        }
      }
    }
  }

  return best;
}

/**
 * Compute the exact hindsight-optimal list (and swap) at a settled MatchDay.
 */
export function computeIdealList(
  gameResults: gameResult[],
  options: ComputeIdealListOptions,
): IdealListResult {
  const matchdayCount = options.matchdayCount ?? MATCHDAY_COUNT;
  const swapMatchday = options.swapMatchday ?? SWAP_MATCHDAY;
  const atMatchday = Math.max(0, Math.min(options.atMatchday, matchdayCount));
  const progressionAwards = options.progressionAwards ?? [];

  const mpm = buildMPM(gameResults, matchdayCount, progressionAwards);
  const cpm = buildCPM(mpm, matchdayCount);

  const sCap = Math.min(swapMatchday, atMatchday);
  const pK = (t: Country) => cpm[t]?.[atMatchday] ?? 0;
  const pPre = (t: Country) => cpm[t]?.[sCap] ?? 0;
  const pPost = (t: Country) => pK(t) - pPre(t);

  const pool = tournamentPoolByCategory();
  const favSorted = sortByPoints(pool.fav, pK);
  const midSorted = sortByPoints(pool.mid, pK);
  const outSorted = sortByPoints(pool.out, pK);

  const empty: IdealListResult = {
    countryList: [],
    swapPair: null,
    score: 0,
    asOfMatchday: atMatchday,
    rows: [],
  };

  if (atMatchday <= 0) return empty;

  let best = bestNoSwap(favSorted, midSorted, outSorted, pK);
  if (!best) return empty;

  if (atMatchday > swapMatchday) {
    const withSwap = bestWithOneSwap(
      pool,
      favSorted,
      midSorted,
      outSorted,
      pPre,
      pPost,
      pK,
    );
    if (withSwap && withSwap.score > best.score) {
      best = withSwap;
    }
  }

  return {
    countryList: best.countryList,
    swapPair: best.swapPair,
    score: best.score,
    asOfMatchday: atMatchday,
    rows: buildIdealListRows({
      countryList: best.countryList,
      swapPair: best.swapPair,
      asOfMatchday: atMatchday,
      cpm,
      swapMatchday,
      mdReset: 0,
    }),
  };
}
